import { ldb, state } from '@codesuma/baseline'
import settings, { Theme } from './settings'
import calendar from './calendar'
import { J_MONTH_COLORS, monthColor, THEME_COLORS } from '../configs/colors'

// What the calendar sits on: the panorama by day, the same scene at night, or
// the month's own seasonal color on its own. The theme is one attribute on
// <html> — every color in the calendar reads from the custom properties defined
// there (see index.html), so flipping it repaints the text and the paper in one
// go.
//
// The panorama is named here rather than in CSS because switching has to decode
// the new art BEFORE anything moves: a dissolve can only cross-fade something
// the browser has already painted once.

// '54, 94, 77' → '#365e4d'. The native side parses a hex string and nothing else.
const hex = (rgb: string) =>
    '#' + rgb.split(',').map(n => Number(n).toString(16).padStart(2, '0')).join('')

// Each theme's browser chrome — Android's PWA bar, the iOS Safari surround.
// It mirrors --paper, but the browser reads it from the meta tag and not from
// CSS, so it has to be a value here. The two flat themes take theirs from the
// channels THEME_COLORS already holds, so those colors live in one place.
const CHROME: Record<Theme, string> = {
    light: hex(THEME_COLORS.light.paper),
    dark: hex(THEME_COLORS.dark.paper),
    paper: hex(THEME_COLORS.paper.paper),
    slate: hex(THEME_COLORS.slate.paper),
    // Only what shows until a month has been drawn — from then on the seasons
    // theme hands its own color over per month, through setPaper below. The
    // first month's color, which is also the stylesheet's own default for this
    // theme (see index.html).
    seasons: hex(J_MONTH_COLORS[0]),
}

// What the next launch should open on, read by the inline script in
// index.html before this bundle exists. Both halves matter: the theme picks
// the ink and every other color with it, and the paper is the one value the
// stylesheet cannot work out on its own under the seasons theme.
const OPENING = 'opening'

// Two paintings of one place, a year wide. Day and night are drawn rather than
// filtered from each other, so each theme simply names its own.
const PANORAMA: Record<Theme, string> = {
    light: '/images/seasons.webp',
    dark: '/images/seasons-dark.webp',
    // The three without art. The two plain ones are the calendar on paper and
    // nothing else; the seasons one has no horizon either, so the month's own
    // color runs all the way up, handed over per month by setPaper below.
    paper: '',
    slate: '',
    seasons: '',
}

// The button walks this loop: the two paintings first, then the same calendar
// with the picture taken away, then the month's own color, then round.
const ORDER: Theme[] = ['light', 'dark', 'paper', 'slate', 'seasons']

// Fetch and decode an image, holding it only until that is done — after which
// the bytes stay in the browser (and service worker) cache.
const load = (src: string) => {
    const img = new Image()
    img.src = src
    return img.decode().catch(() => {})
}

const meta = (color: string) =>
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color)

// Native only: the colour the next launch opens on, handed over now because a
// launch runs before the web view that could tell it — see LaunchPlugin.java.
// The seasons theme has no single paper, and the app always opens on today, so
// today's month is what gets stored and not whichever month was last slid into
// view.
const store = () => {
    const { theme, system } = settings.get()
    const t = calendar.today()
    const rgb = theme === 'seasons'
        ? monthColor(system === 'gregorian' ? t.gm : t.jm, system)
        : THEME_COLORS[theme].paper
    ldb.set(OPENING, { theme, rgb })
    ;(window as any).Capacitor?.Plugins?.Launch?.setColor?.({ color: hex(rgb) })
}

const apply = () => {
    const { theme } = settings.get()
    const root = document.documentElement
    root.dataset.theme = theme
    // A month color left over from the seasons theme would tint the flat
    // themes' paper, so hand the stylesheet its own back.
    root.style.removeProperty('--paper-rgb')
    meta(CHROME[theme])
    // Native: the plugin owns the status/navigation bars. Its DARK means light
    // icons — which is what the night paper and the month colors both want.
    const bars = (window as any).Capacitor?.Plugins?.SystemBars
    bars?.setStyle?.({ style: theme === 'light' || theme === 'paper' ? 'LIGHT' : 'DARK' })
    store()
}

export default {
    // Call before the first render, so the app never paints the wrong theme.
    init() {
        apply()
        state.on('settings', apply)
        // Midnight can carry the seasons theme into a new month, and with it
        // the colour the next launch should open on.
        state.on('today', store)
        // Then pull the OTHER theme's art down in the background. Switching has
        // to decode before it moves, so this is what makes the way across
        // immediate. Left to idle time so it never competes with the first
        // paint.
        const warm = () => {
            const showing = PANORAMA[settings.get().theme]
            for (const art of Object.values(PANORAMA)) {
                if (art && art !== showing) void load(art)
            }
        }
        const idle = (window as any).requestIdleCallback
        idle ? idle(warm, { timeout: 4000 }) : setTimeout(warm, 2000)
    },
    // The art the current theme is drawn on, or '' for the one that has none.
    // The backdrop asks on every theme change.
    panorama: () => PANORAMA[settings.get().theme],
    // The seasons theme has no single paper — it is whichever month is showing.
    // Whoever knows the month calls this; the others never do.
    setPaper(rgb: string) {
        document.documentElement.style.setProperty('--paper-rgb', rgb)
        meta(`rgb(${rgb})`)
    },
    // The settings list picks a theme by name; the button walks the loop.
    toggle() {
        return this.set(ORDER[(ORDER.indexOf(settings.get().theme) + 1) % ORDER.length])
    },
    async set(next: Theme) {
        // Normally already warm (see init), so this returns at once. It stays
        // because the dissolve needs the art decoded before it starts, whatever
        // the cache did — and a failed decode still switches, since a hard cut
        // beats a dead button.
        if (PANORAMA[next]) await load(PANORAMA[next])
        // The dissolve: the browser pictures the old screen, the switch redraws
        // everything once, and the two pictures cross-fade on the GPU (timed in
        // index.html). Easing every color element by element instead held the
        // phone for hundreds of ms a frame. iOS before 18 has no view
        // transitions, and there the switch simply cuts.
        const update = () => settings.update({ theme: next })
        if (document.startViewTransition) document.startViewTransition(update)
        else update()
    },
}
